import type {
  ArchetypeId,
  UniverseCondition,
} from "./types";
import {
  type UniverseConfig,
  type UniverseProfile,
  DEFAULT_CONFIG,
  clampConfig,
  generateUniverse,
  randomConfig,
} from "@/lib/universe";

/**
 * Curated preset universe configurations per archetype that strictly satisfy
 * the archetype predicate requirements defined in docs/wvc-batch-spec.md:
 *
 * 1. divergent:
 *    - near-ours (small perturbation of DEFAULT_CONFIG)
 *    - habitability >= 70
 * 2. ascendant:
 *    - older cosmos (ageGyr high enough, e.g. 24 Gyr)
 *    - habitability >= 50
 *    - stage !== "no stellar era"
 * 3. strange:
 *    - clearly non-standard constants (e.g. higher gravity, stronger EM or strong force)
 *    - habitability 25..65
 *    - timeArrow !== "reversed" (forward time)
 * 4. unexpected:
 *    - habitability >= 40
 *    - non-flat topology or notably different dark energy
 */
export const ARCHETYPE_PRESETS: Record<ArchetypeId, UniverseConfig> = {
  divergent: clampConfig({
    ...DEFAULT_CONFIG,
    gravity: 1.05,
    electromagnetism: 0.98,
    darkEnergy: 1.1,
    matterDensity: 1.0,
    ageGyr: 13.8,
  }),
  ascendant: clampConfig({
    ...DEFAULT_CONFIG,
    gravity: 0.95,
    strongForce: 1.05,
    electromagnetism: 1.02,
    darkEnergy: 1.2,
    matterDensity: 1.1,
    ageGyr: 28.5,
  }),
  strange: clampConfig({
    ...DEFAULT_CONFIG,
    gravity: 2.1,
    strongForce: 1.25,
    electromagnetism: 1.35,
    darkEnergy: 2.5,
    matterDensity: 1.8,
    dimensions: 3,
    topology: "open",
    timeArrow: "forward",
    matterSurplus: 75,
    ageGyr: 11.2,
  }),
  unexpected: clampConfig({
    ...DEFAULT_CONFIG,
    gravity: 1.15,
    strongForce: 1.1,
    darkEnergy: 2.8,
    matterDensity: 1.4,
    topology: "toroidal",
    timeArrow: "forward",
    matterSurplus: 90,
    ageGyr: 16.4,
  }),
};

/**
 * Determine UniverseCondition according to spec:
 * >= 55 -> "hospitable"
 * 20..54 -> "marginal"
 * < 20 -> "hostile"
 */
export function conditionFromHabitability(habitability: number): UniverseCondition {
  if (typeof habitability !== "number" || isNaN(habitability)) return "marginal";
  if (habitability >= 55) return "hospitable";
  if (habitability >= 20) return "marginal";
  return "hostile";
}

/**
 * Archetype predicate check for universe generation.
 */
export function matchesArchetypePredicate(
  archetype: ArchetypeId,
  config: UniverseConfig,
  profile: UniverseProfile
): boolean {
  if (!config || !profile) return false;
  const hab = typeof profile.habitability === "number" ? profile.habitability : 0;
  switch (archetype) {
    case "divergent": {
      // near-ours (small perturbation of DEFAULT_CONFIG, habitability >= 70)
      const isNearOurs =
        Math.abs((config.gravity ?? 1) - 1) <= 0.35 &&
        Math.abs((config.strongForce ?? 1) - 1) <= 0.25 &&
        Math.abs((config.electromagnetism ?? 1) - 1) <= 0.25 &&
        config.dimensions === 3 &&
        config.timeArrow === "forward";
      return isNearOurs && hab >= 70;
    }
    case "ascendant": {
      // older, star-bearing (ageGyr high enough, habitability >= 50, stage not "no stellar era")
      return (config.ageGyr ?? 0) >= 20 && hab >= 50 && profile.stage !== "no stellar era";
    }
    case "strange": {
      // clearly non-standard constants, habitability 25..65, not reversed time arrow
      const nonStandard =
        (config.gravity ?? 1) > 1.4 ||
        (config.gravity ?? 1) < 0.7 ||
        (config.strongForce ?? 1) > 1.2 ||
        (config.strongForce ?? 1) < 0.8 ||
        (config.electromagnetism ?? 1) > 1.2 ||
        (config.electromagnetism ?? 1) < 0.8 ||
        config.topology !== "flat" ||
        (config.darkEnergy ?? 1) > 2.0;
      return (
        nonStandard &&
        config.timeArrow === "forward" &&
        hab >= 25 &&
        hab <= 65
      );
    }
    case "unexpected": {
      // habitability >= 40, non-flat topology or notably different dark energy
      const unusualSpace = config.topology !== "flat" || (config.darkEnergy ?? 1) >= 2.2 || (config.darkEnergy ?? 1) <= 0.2;
      return unusualSpace && hab >= 40;
    }
    default:
      return false;
  }
}

/**
 * Seeded PRNG for bounded universe search (mulberry32).
 */
function createMulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Pick or generate a universe for an archetype deterministically.
 * If candidate attempts do not satisfy the predicate within 25 tries,
 * falls back cleanly to the curated ARCHETYPE_PRESETS[archetype].
 */
export function pickUniverseForArchetype(
  archetype: ArchetypeId,
  seed: number,
  sharedUniverseConfig: UniverseConfig | null
): { config: UniverseConfig; profile: UniverseProfile; condition: UniverseCondition } {
  if (sharedUniverseConfig) {
    try {
      const config = clampConfig(sharedUniverseConfig);
      const profile = generateUniverse(config);
      return {
        config,
        profile,
        condition: conditionFromHabitability(profile.habitability),
      };
    } catch (err) {
      console.error("Failed to generate shared universe, falling back to preset", err);
    }
  }

  const validArchetype = ARCHETYPE_PRESETS[archetype] ? archetype : "divergent";
  const rng = createMulberry32(seed ^ 0x9e3779b9);

  // Attempt up to 25 seeded candidate draws
  for (let attempt = 0; attempt < 25; attempt++) {
    let candidate: UniverseConfig;
    if (validArchetype === "divergent") {
      // Perturb default config slightly
      candidate = clampConfig({
        ...DEFAULT_CONFIG,
        gravity: 1 + (rng() - 0.5) * 0.3,
        strongForce: 1 + (rng() - 0.5) * 0.2,
        electromagnetism: 1 + (rng() - 0.5) * 0.2,
        darkEnergy: 0.8 + rng() * 0.5,
        matterDensity: 0.8 + rng() * 0.5,
        ageGyr: 12 + rng() * 4,
      });
    } else if (validArchetype === "ascendant") {
      candidate = clampConfig({
        ...DEFAULT_CONFIG,
        gravity: 0.85 + rng() * 0.35,
        strongForce: 0.95 + rng() * 0.2,
        electromagnetism: 0.95 + rng() * 0.2,
        darkEnergy: 0.8 + rng() * 1.5,
        matterDensity: 0.9 + rng() * 0.8,
        ageGyr: 22 + rng() * 25,
      });
    } else if (validArchetype === "strange") {
      const topPool = ["flat", "open", "toroidal"] as const;
      candidate = clampConfig({
        gravity: 1.5 + rng() * 1.8,
        strongForce: 1.1 + rng() * 0.4,
        electromagnetism: 1.15 + rng() * 0.35,
        darkEnergy: 1.5 + rng() * 3.0,
        matterDensity: 1.2 + rng() * 1.5,
        dimensions: 3,
        topology: topPool[Math.floor(rng() * topPool.length)],
        timeArrow: "forward",
        matterSurplus: 60 + Math.floor(rng() * 35),
        ageGyr: 8 + rng() * 10,
      });
    } else {
      // unexpected
      const nonFlatPool = ["closed", "open", "toroidal"] as const;
      candidate = clampConfig({
        gravity: 0.8 + rng() * 0.6,
        strongForce: 0.9 + rng() * 0.3,
        electromagnetism: 0.9 + rng() * 0.3,
        darkEnergy: 2.2 + rng() * 3.5,
        matterDensity: 0.8 + rng() * 1.2,
        dimensions: 3,
        topology: nonFlatPool[Math.floor(rng() * nonFlatPool.length)],
        timeArrow: "forward",
        matterSurplus: 70 + Math.floor(rng() * 30),
        ageGyr: 14 + rng() * 15,
      });
    }

    try {
      const profile = generateUniverse(candidate);
      if (matchesArchetypePredicate(validArchetype, candidate, profile)) {
        return {
          config: candidate,
          profile,
          condition: conditionFromHabitability(profile.habitability),
        };
      }
    } catch {
      // continue candidate search
    }
  }

  // Fallback to verified preset
  const presetConfig = ARCHETYPE_PRESETS[validArchetype] ?? ARCHETYPE_PRESETS.divergent;
  const presetProfile = generateUniverse(presetConfig);
  return {
    config: presetConfig,
    profile: presetProfile,
    condition: conditionFromHabitability(presetProfile.habitability),
  };
}
